import type {
  AuthDeliveryMedium,
  AuthNextSignInStep,
  AuthUserAttributeKey,
} from '@aws-amplify/auth/types';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {emptySplitApi} from 'src/redux/api';
import type {
  ConfirmResetPasswordInput,
  ConfirmSignInInput,
  ConfirmSignUpInput,
  ResetPasswordOutput,
  SignInInput,
} from 'aws-amplify/auth';
import {
  autoSignIn,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import type {UserValidationStatusEnum} from 'src/api/ProactApiClient';

export enum PasswordResetState {
  NotStarted = 0,
  PendingConfirmationCode = 1,
  Done = 2,
}

export interface AuthState {
  isError: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  isDomainChecked: boolean;
  identityProvider: string | null;
  username: string | null;
  userAccountStatus: UserValidationStatusEnum | null;
  userMfaEnabled: boolean;
  isSignedIn: boolean;
  signInNextStep: AuthNextSignInStep<AuthUserAttributeKey> | null;
  message: string;
  passwordResetStep: PasswordResetState;
  passwordResetMedium?: AuthDeliveryMedium;
  accessToken?: string;
}

// Async thunk for refreshAccessTokenCache
export const refreshAccessTokenCache = createAsyncThunk(
  'auth/refreshAccessTokenCache',
  async (_, thunkAPI) => {
    try {
      const {accessToken} = (await fetchAuthSession()).tokens ?? {};
      return accessToken?.toString();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Async thunk for Confirming Sign-Up, after an admin creates an account
export const confirmSignUpUser = createAsyncThunk(
  'auth/confirmSignUpUser',
  async (data: ConfirmSignUpInput, thunkAPI) => {
    try {
      const output = await confirmSignUp(data);

      switch (output.nextStep.signUpStep) {
        case 'DONE':
        case 'CONFIRM_SIGN_UP':
        case 'COMPLETE_AUTO_SIGN_IN':
          // As of now, any response wil trigger AutoSignin to generate the
          // appropriate access token.
          return await autoSignIn();

        default:
          throw 'Invalid Next SignUp Step';
      }
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
// Async thunk for Sign-In
export const signInUser = createAsyncThunk(
  'auth/signIn',
  async (userData: SignInInput, thunkAPI) => {
    // We force the user to sign out first to prevent a Cognito bug where the user is
    // perceived to be signed in even though they do not have a valid access token.
    // This is a Cognito-only operation and bypasses the regular signout flows.
    try {
      await signOut();
    } catch {
      /* empty */
    }

    try {
      const signInOutput = await signIn(userData);

      return signInOutput;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Async thunk for SSO Sign-In via hosted UI redirect
export const signInWithRedirectUser = createAsyncThunk(
  'auth/signInWithRedirect',
  async (identityProvider: string, thunkAPI) => {
    // We force the user to sign out first to prevent a Cognito bug where the user is
    // perceived to be signed in even though they do not have a valid access token.
    // This is a Cognito-only operation and bypasses the regular signout flows.
    try {
      await signOut();
    } catch {
      /* empty */
    }

    try {
      // On success this navigates the browser away to the identity provider, so
      // the fulfilled state below is generally never observed.
      await signInWithRedirect({provider: {custom: identityProvider}});
      return null;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Async thunk for Confirming Sign-In
export const confirmSignInUser = createAsyncThunk(
  'auth/confirmSignInUser',
  async (data: ConfirmSignInInput, thunkAPI) => {
    try {
      const output = await confirmSignIn(data);

      return output;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Async thunk for Confirming Sign-Up, after an admin creates an account
export const autoSignInUser = createAsyncThunk(
  'auth/autoSignInUser',
  async (_, thunkAPI) => {
    try {
      return await autoSignIn();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Async thunk for Sign-Out
export const signOutUser = createAsyncThunk(
  'auth/signOut',
  async (_, thunkAPI) => {
    try {
      await signOut();
      thunkAPI.dispatch(emptySplitApi.util.resetApiState());
      return null;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Async thunk for Reset Password
export const resetUserPassword = createAsyncThunk(
  'auth/resetPassword',
  async (username: string, thunkAPI) => {
    try {
      return await resetPassword({username});
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Async thunk for Confirm Password
export const confirmUserPassword = createAsyncThunk(
  'auth/confirmPassword',
  async (data: ConfirmResetPasswordInput, thunkAPI) => {
    try {
      await confirmResetPassword(data);

      return null;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState: AuthState = {
  isError: false,
  isSuccess: false,
  isLoading: false,
  isDomainChecked: false,
  identityProvider: null,
  username: null,
  userAccountStatus: null,
  userMfaEnabled: false,
  isSignedIn: false,
  signInNextStep: null,
  message: '',
  passwordResetStep: PasswordResetState.NotStarted,
  passwordResetMedium: undefined,
  accessToken: undefined,
};

// Slice
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: () => initialState,
    resetAuthErrors: state => {
      state.message = '';
      state.isError = false;
      state.isLoading = false;
    },
    resetSignInNextStep: state => {
      state.signInNextStep = null;
      state.isSignedIn = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = '';
    },
    setIdentityProvider: (state, action: PayloadAction<string>) => {
      state.identityProvider = action.payload;
    },
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    setIsDomainChecked: (state, action: PayloadAction<boolean>) => {
      state.isDomainChecked = action.payload;
    },
    setUserAccountStatus: (
      state,
      action: PayloadAction<UserValidationStatusEnum>
    ) => {
      state.userAccountStatus = action.payload;
    },
    setUserMfaEnabled: (state, action: PayloadAction<boolean>) => {
      state.userMfaEnabled = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(refreshAccessTokenCache.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(refreshAccessTokenCache.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.accessToken = action.payload as string;
      })
      .addCase(refreshAccessTokenCache.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(autoSignInUser.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(autoSignInUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isSignedIn = action.payload.isSignedIn;
        state.signInNextStep = action.payload.nextStep;
      })
      .addCase(autoSignInUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(confirmSignUpUser.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(confirmSignUpUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isSignedIn = action.payload.isSignedIn;
        state.signInNextStep = action.payload.nextStep;
      })
      .addCase(confirmSignUpUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(signInUser.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isSignedIn = action.payload.isSignedIn;
        state.signInNextStep = action.payload.nextStep;
      })
      .addCase(signInUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(signInWithRedirectUser.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(signInWithRedirectUser.fulfilled, state => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(signInWithRedirectUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(confirmSignInUser.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(confirmSignInUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isSignedIn = action.payload.isSignedIn;
        state.signInNextStep = action.payload.nextStep;
      })
      .addCase(confirmSignInUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(signOutUser.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(signOutUser.fulfilled, state => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(signOutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(resetUserPassword.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(
        resetUserPassword.fulfilled,
        (state, action: PayloadAction<ResetPasswordOutput>) => {
          state.isLoading = false;
          state.isSuccess = true;

          if (
            action.payload.nextStep.resetPasswordStep ===
            'CONFIRM_RESET_PASSWORD_WITH_CODE'
          ) {
            state.passwordResetStep =
              PasswordResetState.PendingConfirmationCode;
            state.passwordResetMedium =
              action.payload.nextStep.codeDeliveryDetails.deliveryMedium;
          } else if (action.payload.nextStep.resetPasswordStep === 'DONE') {
            state.passwordResetStep = PasswordResetState.Done;
            state.passwordResetMedium = undefined;
          }
        }
      )
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(confirmUserPassword.pending, state => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = '';
      })
      .addCase(confirmUserPassword.fulfilled, state => {
        state.isLoading = false;
        state.isSuccess = true;
        state.passwordResetStep = PasswordResetState.Done;
        state.passwordResetMedium = undefined;
      })
      .addCase(confirmUserPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
        state.passwordResetMedium = undefined;
      });
  },
});

export const {
  resetAuthState,
  resetAuthErrors,
  resetSignInNextStep,
  setIdentityProvider,
  setUsername,
  setIsDomainChecked,
  setUserAccountStatus,
  setUserMfaEnabled,
} = authSlice.actions;

export default authSlice.reducer;
